import { REPORT_REASONS, type ReportReason } from '@trendator/shared';
import { useState } from 'react';
import { ApiError, api } from '../api';
import { haptic, hapticResult } from '../telegram';

/**
 * Жалоба: на результат генерации или «моё фото использовали без согласия».
 * Уходит в очередь модерации, ответ — в течение суток.
 */
export function ReportSheet({ generationId, reason: preset, onClose }: { generationId?: string; reason?: ReportReason; onClose: () => void }) {
  const [reason, setReason] = useState<ReportReason>(preset ?? (generationId ? 'nsfw' : 'my_face'));
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needText = reason === 'other' || (reason === 'my_face' && !generationId);

  const send = async () => {
    if (busy || (needText && text.trim().length < 5)) return;
    haptic();
    setBusy(true);
    setError(null);
    try {
      await api.report({ reason, text: text.trim(), ...(generationId ? { generationId } : {}) });
      hapticResult('success');
      setSent(true);
    } catch (err) {
      hapticResult('error');
      setError(err instanceof ApiError && err.code === 'too_many_reports' ? 'Слишком много жалоб за сутки — мы уже разбираемся.' : 'Не отправилось — попробуй ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-label="Пожаловаться" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__grip" />
        {sent ? (
          <>
            <div className="sheet__head">
              <h2 className="sheet__title">Спасибо, получили</h2>
              <p className="sheet__text">Проверим в течение суток. Если подтвердится — удалим и заблокируем нарушителя.</p>
            </div>
            <button type="button" className="btn-accent" style={{ marginTop: 16 }} onClick={onClose}>
              Хорошо
            </button>
          </>
        ) : (
          <>
            <div className="sheet__head">
              <h2 className="sheet__title">{generationId ? 'Пожаловаться на результат' : 'Моё фото использовали без согласия'}</h2>
              <p className="sheet__text">
                {generationId ? 'Что не так? Модератор посмотрит и примет меры.' : 'Опиши, где и как: ссылка, имя в Telegram, дата. Мы найдём и удалим.'}
              </p>
            </div>
            {generationId ? (
              <div className="sheet__options">
                {REPORT_REASONS.map((r) => (
                  <label key={r.id} className={`sheet-option${r.id === reason ? ' sheet-option--on' : ''}`}>
                    <span className="sheet-option__text">
                      <span className="sheet-option__hint" style={{ color: 'inherit' }}>
                        {r.label}
                      </span>
                    </span>
                    <input
                      type="radio"
                      name="reason"
                      checked={r.id === reason}
                      onChange={() => {
                        haptic('select');
                        setReason(r.id);
                      }}
                    />
                  </label>
                ))}
              </div>
            ) : null}
            <textarea
              className="report__text"
              rows={3}
              maxLength={1000}
              placeholder={needText ? 'Опиши подробнее' : 'Комментарий (необязательно)'}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            {error ? <p className="error-text" role="alert" style={{ marginTop: 10 }}>{error}</p> : null}
            <button
              type="button"
              className="btn-accent"
              style={{ marginTop: 12 }}
              disabled={busy || (needText && text.trim().length < 5)}
              onClick={() => void send()}
            >
              {busy ? 'Отправляем…' : 'Отправить'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Ссылка «Пожаловаться» под результатом. */
export function ReportLink({ generationId }: { generationId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="link-btn report__link" onClick={() => setOpen(true)}>
        Пожаловаться
      </button>
      {open ? <ReportSheet generationId={generationId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
